// @noflow
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { countryCodeToPrivacyUrl } from '@/utils/countryCodeHelper'

import type { CookiePreferences } from '@/components/cookie_consent/index'
import {
  BaseModalProps,
  Variant
} from '@/components/shared/CookieConsent/Modals/CookieConsentModal'
import {
  Component,
  Context,
  actionToCookieConsent,
  manageCookiesClicked,
  trackCookiePreferences
} from '@/components/shared/CookieConsent/helpers'
import { Action } from '@/components/shared/CookieConsent/types'

type StrictProps = {
  variant: Variant
  applyPreferences: (preferences: CookiePreferences) => void
  closeBanner?: never
}

type StrictModalProps = BaseModalProps

const copyContext = 'cookie_consent'

const StrictModal = ({
  variant,
  shippingCountryCode,
  applyPreferences,
  setShowStrictModal,
  handleModalClose
}: StrictModalProps): JSX.Element => {
  const { t } = useTranslation('shared')

  const handleOnDeny = useCallback(() => {
    actionToCookieConsent({ action: Action.Deny, applyPreferences })

    manageCookiesClicked()

    handleModalClose()
  }, [applyPreferences, handleModalClose])

  // Switches to the Manage Modal
  const handleOnManage = useCallback(() => {
    setShowStrictModal(false)
    manageCookiesClicked()
  }, [setShowStrictModal])

  const handleOnAcceptAll = useCallback(() => {
    actionToCookieConsent({ action: Action.Accepted, applyPreferences })

    trackCookiePreferences({
      preferences: { marketing: true, analytics: true },
      variant,
      component: Component.Modal,
      context: Context.AcceptAll
    })

    handleModalClose()
  }, [applyPreferences, handleModalClose, variant])

  return (
    <>
      <div className="cookie-modal__info">
        <h2>{t(`${copyContext}.banner.title`)}</h2>
        <p
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t(`${copyContext}.banner.description_strict`, {
              privacyUrl: countryCodeToPrivacyUrl(shippingCountryCode)
            })
          }}
        />
      </div>
      <div className="btn-group">
        <button
          className="btn secondary"
          type="button"
          onClick={handleOnManage}
        >
          {t(`${copyContext}.banner.manage_btn`)}
        </button>
        <button className="btn secondary" type="button" onClick={handleOnDeny}>
          {t(`${copyContext}.banner.deny_btn`)}
        </button>
        <button
          id="cookie-banner-accept-button"
          data-testid="cookie-accept-strict-button"
          className="btn primary"
          type="button"
          onClick={handleOnAcceptAll}
        >
          {t(`${copyContext}.modal.accept_btn`)}
        </button>
      </div>
    </>
  )
}

export default StrictModal

export { StrictProps }
